import React from "react";
import PropTypes from "prop-types";

import { Transition } from "react-transition-group";
import { Portal } from "react-portal";

import { t, I18n } from "lib/I18n";
import UnitReward from "./UnitReward";
import RewardDetails from "./RewardDetails";
import SuperbadgeReward from "./SuperbadgeReward";
import NextRewardButton from "./NextRewardButton";
import NextChallengeButton from "./NextChallengeButton";
import RewardConfetti from "./RewardConfetti";
import WebComponent from "../WebComponent";
import GtmTracker, { transformPayload } from "../../lib/tracking/trackGtm";

const isType =
  (expectedType) =>
  ({ type }) =>
    expectedType === type;
const isTypeEventBadge = isType("Th::EventBadge");
const isTypeSuperBadge = isType("Th::Superbadge");

const parents = {
  unit: "module",
  step: "project"
};

const duration = 300;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0
};

const transitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1 }
};

function isBadge({ type }) {
  return type === "Th::ModuleBadge" || type === "Th::Superbadge";
}

export default class RewardsModalContent extends React.Component {
  static propTypes = {
    rewards: PropTypes.array.isRequired,
    nextChallenge: PropTypes.object.isRequired,
    showRecommendations: PropTypes.bool,
    badgeRatings: PropTypes.object.isRequired,
    colorScheme: PropTypes.string.isRequired,
    sharing: PropTypes.object.isRequired,
    moduleApiName: PropTypes.string.isRequired,
    unitType: PropTypes.string.isRequired,
    modalRef: PropTypes.object,
    hasNewRecommendations: PropTypes.bool,
    fromLearningPath: PropTypes.bool,
    fromLearnerTrail: PropTypes.bool,
    learningPathUri: PropTypes.string,
    wasRetake: PropTypes.bool,
  };

  state = {
    rewardIdx: 0
  };

  componentDidMount() {
    // requestAnimationFrame is needed otherwise modalRef.current is always null!
    requestAnimationFrame(() => {
      this.setLastPageCtaProp();
    });
  }

  // Ensures that if the recommendations loads without content or has an error that the last page CTA updates as well
  componentDidUpdate(prevProps) {
    if (!prevProps.hasNewRecommendations && this.props.hasNewRecommendations) {
      this.setLastPageCtaProp();
    }
  }

  lastPageCtaConfig = () => {
    if(this.props.hasNewRecommendations) {
      return {
        href: t("reward.button.recommended_content_cta_url"),
        label: t("reward.button.recommended_content_cta")
      }
    }

    if (this.props.fromLearningPath && !this.props.nextChallenge.next_challenge) {
      return {
        href: this.props.learningPathUri,
        label: t(this.props.fromLearnerTrail ? "reward.button.return_to_trailmix" : "reward.button.return_to_trail"),
        header: t('reward.encourage'),
        description: "",
      }
    }

    if (this.state.rewardIdx < this.filteredRewards().length - 1) {
      return {
        href: this.nextReward,
        label: t('reward.button.continue')
      }
    } else {
      const unitType = this.props.rewards[this.state.rewardIdx].type === "Th::Step" ? "step" : "unit";
      const nextChallengeConfig = new NextChallengeButton({unitType: unitType, nextChallenge: this.props.nextChallenge});
      const { label, href, header, description } = nextChallengeConfig.config();

      return {
        header: header,
        description: description,
        href: href,
        label: label
      }
    }
  }

  setLastPageCtaProp = () => {
    const lastPageCta = this.lastPageCtaConfig();
    const { modalRef } = this.props;

    if (modalRef && modalRef.current) {
      modalRef.current.lastPageCta = lastPageCta ? { ...lastPageCta } : null;
    }
  };

  trackInteraction = (e, eventAction) => {
    const payload = {
      eventLabel: `${e.detail.contentType}.${e.detail.apiName}`,
      eventCategory: "favorites",
      eventAction
    };

    new GtmTracker().track(transformPayload(payload));
  };

  canShowRateBadge() {
    const { badgeRatings } = this.props;
    return this.props.rewards.some(isBadge) && badgeRatings.show_badge_ratings === true;
  }

  filteredRewards = () => {
    if (this.props.rewards.some((r) => r.type === "Th::ModuleBadge")) {
      return this.props.rewards.filter((r) => r.type !== "Th::Unit" && r.type !== "Th::Step");
    }
    if (this.props.rewards.some((r) => r.type === "Th::Superbadge")) {
      return this.props.rewards.filter((r) => r.type !== "Th::Section");
    }
    return this.props.rewards;
  };

  sharingProps = (reward) => (reward.type === "Th::ModuleBadge" ? this.props.sharing : null);

  nextReward = () => {
    this.setState({ rewardIdx: this.state.rewardIdx + 1 });
  };

  subtitle() {
    return t("reward.module.subheading_full_screen");
  }

  renderReward = (reward) => {
    const { modalRef } = this.props;

    switch (reward.type) {
      case "Th::Unit":
      case "Th::Step":
      case "Th::Section":
        return <UnitReward reward={reward} wasRetake={this.props.wasRetake} />;
      case "Th::Superbadge":
        return (
          <SuperbadgeReward
            reward={reward}
            next={this.props.nextChallenge}
            sharing={this.props.sharing}
            modalRef={modalRef}
            wasRetake={this.props.wasRetake}
          />
        );
      default: {
        const title = reward.type === "Th::EventBadge" ? t("reward.event.heading") : t("reward.module.heading");

        return (
          <RewardDetails
            title={title}
            subtitle={this.subtitle()}
            badgeName={reward.title}
            iconUrl={reward.badge_url}
            assistiveText={{ iconLabel: t("reward.module.complete") }}
            sharing={this.sharingProps(reward)}
            modalRef={modalRef}
          />
        );
      }
    }
  };

  renderSlide = (reward, idx) => {
    const show = idx === this.state.rewardIdx;

    return (
      <Transition key={`reward_${idx}`} in={show} timeout={500} mountOnEnter unmountOnExit exit={false}>
        {(state) => (
          <div
            style={{
              ...defaultStyle,
              ...transitionStyles[state]
            }}
          >
            {this.renderReward(reward)}
          </div>
        )}
      </Transition>
    );
  };

  renderModalBody = () => <div className="slds-p-around_medium">{this.filteredRewards().map(this.renderSlide)}</div>;

  modalFooterMargin() {
    return "slds-p-top_x-large";
  }

  renderConfetti = () => {
    if (this.props.rewards.some(isBadge)) {
      return (
        <Portal>
          <RewardConfetti colorScheme={this.props.colorScheme} />
        </Portal>
      );
    }
    return "";
  };

  renderRateBadge = () => {
    if (!this.canShowRateBadge()) {
      return null;
    }
    const { badgeRatings } = this.props;

    const attrs = {
      apiName: badgeRatings.api_name,
      trackable: badgeRatings.trackable
    };

    return (
      <div className="slds-p-around_small">
        <WebComponent tagName="th-badge-rating" attrs={attrs} />
      </div>
    );
  };

  getButtonStyle() {
    return {
      margin: "0 -16px",
      display: "flex",
      justifyContent: "center",
    };
  }

  render() {
    return (
      <div>
        {this.renderModalBody()}
        {this.renderRateBadge()}
        {this.renderConfetti()}
      </div>
    );
  }
}
